/**
 * Footer — Insolip brand (logo, tagline, compact layout)
 */
import { motion } from 'motion/react';
import { LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { content } from '@/landing/data/content';

interface FooterProps {
  onAccessPlatform?: () => void;
}

export function Footer({ onAccessPlatform }: FooterProps) {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer
      className="relative overflow-hidden border-t border-[rgba(201,168,76,0.25)]"
      style={{
        background: 'linear-gradient(135deg, var(--royal) 0%, #0f3a8a 50%, var(--navy) 100%)',
      }}
    >
      <div className="absolute inset-0 hero-grid-overlay opacity-50" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="py-10 grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo & Tagline */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <img src="/images/Corrected%20Logo.png" alt="Insolip" className="h-16 w-auto mb-3" />
              <p className="text-[var(--text-muted)] font-light leading-relaxed">
                {content.tagline}
              </p>
              {onAccessPlatform && (
                <Button onClick={onAccessPlatform} className="mt-4 insolip-cta-primary">
                  <LogIn className="w-4 h-4 mr-2" />
                  Access Platform
                </Button>
              )}
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <h3 className="font-display text-lg font-semibold mb-3 text-white">Navigation</h3>
            <ul className="space-y-2">
              {content.footerLinks.navigation.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                    className="text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors inline-block hover:translate-x-1 duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <h3 className="font-display text-lg font-semibold mb-3 text-white">Services</h3>
            <ul className="space-y-2">
              {content.footerLinks.services.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => { if (link.href.startsWith('#')) { e.preventDefault(); scrollToSection(link.href); } }}
                    className="text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors inline-block hover:translate-x-1 duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
            <h3 className="font-display text-lg font-semibold mb-3 text-white">Connect</h3>
            <ul className="space-y-2">
              {content.footerLinks.connect.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => { if (link.href.startsWith('#')) { e.preventDefault(); scrollToSection(link.href); } }}
                    className="text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors inline-block hover:translate-x-1 duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="border-t border-[rgba(255,255,255,0.1)] py-5"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[var(--text-muted)] text-sm">
              © {currentYear} INSOLIP. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              {content.footerLinks.legal.map((link, index) => (
                <span key={link.label} className="flex items-center gap-6">
                  <a href={link.href} className="text-[var(--text-muted)] hover:text-white transition-colors text-sm">
                    {link.label}
                  </a>
                  {index < content.footerLinks.legal.length - 1 && <span className="text-white/30">•</span>}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="pb-5 text-center space-y-1">
          <p className="text-[var(--text-muted)] text-sm">
            powered by Innobid
          </p>
          <p className="text-[var(--text-muted)] text-xs">
            {content.contact.phone} · {content.contact.email}
          </p>
        </motion.div>
      </div>
    </footer>
  );
}
